// src/components/roles/RoleWizard/RoleWizard.hooks.ts
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { RoleWizardData } from '../../../types/role.types';
import {
  initializeEmptyPermissions,
  convertPermissionsToAPI,
} from '../../../utils/permissions.utils';
import {
  menuBindsViewRowToMenuVisibility,
  syncBoundViewRowForMenuPermissions,
  MENU_VIEW_ROW_ACTION_ID,
} from '../../../config/system-structure';

export interface UseRoleWizardProps {
  initialData?: Partial<RoleWizardData>;
  onSave: (data: RoleWizardData) => Promise<void>;
  onClose: () => void;
  isOpen: boolean; // Agregar isOpen para detectar cuando se cierra
}

export interface UseRoleWizardReturn {
  // Step management
  currentStep: number;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;

  // Form data
  formData: RoleWizardData;
  setFormData: React.Dispatch<React.SetStateAction<RoleWizardData>>;

  // Permissions management
  toggleModule: (moduleId: string) => void;
  toggleMenu: (moduleId: string, menuId: string) => void;
  toggleMenuVisibility: (moduleId: string, menuId: string) => void;
  toggleMainButton: (moduleId: string, menuId: string, buttonId: string) => void;
  toggleRowAction: (moduleId: string, menuId: string, actionId: string) => void;
  toggleColumnFlag: (moduleId: string, menuId: string, flagId: string) => void;
  toggleSalesFlowAction: (moduleId: string, menuId: string, actionId: string) => void;
  selectAllMainButtons: (moduleId: string, menuId: string) => void;
  selectAllRowActions: (moduleId: string, menuId: string) => void;

  // Computed values
  totalPermissions: number;
  enabledModules: number;
  enabledMenus: number;

  // Actions
  handleSave: () => Promise<void>;
  handleReset: () => void;
  handleCloseClick: () => void;
  cancelOperation: () => void;
  showCancelConfirmation: boolean;
  setShowCancelConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  hasChanges: boolean; // Para detectar si hay cambios sin guardar
}

// Usar función de utilidades para inicializar permisos
const INITIAL_PERMISSIONS = initializeEmptyPermissions();

const INITIAL_FORM_DATA: RoleWizardData = {
  name: '',
  description: '',
  permissions: JSON.parse(JSON.stringify(INITIAL_PERMISSIONS)), // Deep copy
};

export const useRoleWizard = ({
  initialData,
  onSave,
  onClose,
  isOpen,
}: UseRoleWizardProps): UseRoleWizardReturn => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Guardar el estado inicial para comparar cambios
  const initialFormDataRef = useRef<RoleWizardData | null>(null);
  const isEditModeRef = useRef<boolean>(false);
  // Rastrear si los permisos ya se cargaron para evitar actualizaciones innecesarias
  const permissionsLoadedRef = useRef<boolean>(false);
  // Rastrear si el usuario ya modificó permisos para no sobrescribirlos al cargar
  const permissionsTouchedRef = useRef<boolean>(false);
  // Rastrear el rol actual para evitar resets innecesarios
  const currentRoleKeyRef = useRef<string | null>(null);
  // Rastrear el último formData para comparar cambios del usuario sin incluir en dependencias
  const lastFormDataRef = useRef<string>('');

  const [formData, setFormData] = useState<RoleWizardData>(() => {
    if (initialData) {
      const data = {
        name: initialData.name || '',
        description: initialData.description || '',
        permissions: initialData.permissions || JSON.parse(JSON.stringify(INITIAL_PERMISSIONS)),
      };
      initialFormDataRef.current = JSON.parse(JSON.stringify(data));
      lastFormDataRef.current = JSON.stringify(data);
      isEditModeRef.current = true;
      return data;
    }
    const data = JSON.parse(JSON.stringify(INITIAL_FORM_DATA));
    initialFormDataRef.current = JSON.parse(JSON.stringify(data));
    lastFormDataRef.current = JSON.stringify(data);
    isEditModeRef.current = false;
    return data;
  });

  // Resetear formulario cuando se cierra el wizard
  useEffect(() => {
    if (!isOpen) {
      // Resetear todo cuando el wizard se cierra
      const resetData = JSON.parse(JSON.stringify(INITIAL_FORM_DATA));
      setFormData(resetData);
      setCurrentStep(1);
      setShowCancelConfirmation(false);
      initialFormDataRef.current = JSON.parse(JSON.stringify(INITIAL_FORM_DATA));
      isEditModeRef.current = false;
      permissionsLoadedRef.current = false;
      permissionsTouchedRef.current = false;
      currentRoleKeyRef.current = null;
      lastFormDataRef.current = JSON.stringify(resetData);
    } else if (isOpen) {
      // Cuando se abre el wizard, resetear flags para permitir detección de carga de permisos
      // Esto asegura que siempre detecte cuando los permisos se cargan, incluso si se abre el mismo rol
      permissionsLoadedRef.current = false;
    }
  }, [isOpen, initialData]);

  // Update form data when initialData changes (solo cuando el wizard está abierto)
  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      const roleKey = initialData.name || null;
      const isNewRole = currentRoleKeyRef.current !== roleKey;
      const baseData = {
        name: initialData.name || '',
        description: initialData.description || '',
        permissions: initialData.permissions || JSON.parse(JSON.stringify(INITIAL_PERMISSIONS)),
      };

      if (isNewRole) {
        currentRoleKeyRef.current = roleKey;
        permissionsTouchedRef.current = false;
        permissionsLoadedRef.current = !!initialData.permissions;

        setFormData(baseData);
        initialFormDataRef.current = JSON.parse(JSON.stringify(baseData));
        lastFormDataRef.current = JSON.stringify(baseData);
        isEditModeRef.current = true;
        setCurrentStep(1);
      } else {
        const hasRealPermissions =
          initialData.permissions &&
          JSON.stringify(initialData.permissions) !== JSON.stringify(INITIAL_PERMISSIONS);

        if (hasRealPermissions && !permissionsLoadedRef.current && !permissionsTouchedRef.current) {
          setFormData((prev) => {
            const newData = { ...prev, permissions: baseData.permissions };
            lastFormDataRef.current = JSON.stringify(newData);
            return newData;
          });

          if (initialFormDataRef.current) {
            initialFormDataRef.current = {
              ...initialFormDataRef.current,
              permissions: JSON.parse(JSON.stringify(baseData.permissions)),
            };
          }
          permissionsLoadedRef.current = true;
        }
      }
    } else {
      // Reset para modo creación
      currentRoleKeyRef.current = null;
      permissionsTouchedRef.current = false;
      permissionsLoadedRef.current = false;

      const data = JSON.parse(JSON.stringify(INITIAL_FORM_DATA));
      setFormData(data);
      initialFormDataRef.current = JSON.parse(JSON.stringify(data));
      lastFormDataRef.current = JSON.stringify(data);
      isEditModeRef.current = false;
      setCurrentStep(1);
    }

    setShowCancelConfirmation(false);
  }, [initialData, isOpen]);

  // Actualizar lastFormDataRef cuando formData cambia (para detectar cambios del usuario en paso 1)
  // Esto previene que el useEffect de initialData sobrescriba los cambios del usuario
  useEffect(() => {
    // Solo actualizar si el wizard está abierto y no estamos en el proceso de inicialización
    if (isOpen && initialFormDataRef.current) {
      lastFormDataRef.current = JSON.stringify(formData);
    }
  }, [formData, isOpen]);

  // Step management
  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 3) {
      setCurrentStep(step);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const canGoNext = useMemo(() => {
    if (currentStep === 1) {
      return formData.name.trim().length >= 3;
    }
    if (currentStep === 2) {
      return true; // Always can go next from permissions
    }
    return false;
  }, [currentStep, formData.name]);

  const canGoPrev = useMemo(() => {
    return currentStep > 1;
  }, [currentStep]);

  // Permissions management
  const toggleModule = useCallback((moduleId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const module = newData.permissions[moduleId];
      if (module) {
        module.enabled = !module.enabled;
        // If disabling module, disable all menus
        if (!module.enabled) {
          Object.keys(module.menus).forEach((menuId) => {
            module.menus[menuId].visible = false;
            Object.keys(module.menus[menuId].mainButtons).forEach((buttonId) => {
              module.menus[menuId].mainButtons[buttonId] = false;
            });
            Object.keys(module.menus[menuId].rowActions).forEach((actionId) => {
              module.menus[menuId].rowActions[actionId] = false;
            });
            const m = module.menus[menuId] as {
              columnFlags?: Record<string, boolean>;
              salesFlowActions?: Record<string, boolean>;
            };
            if (m.columnFlags) {
              Object.keys(m.columnFlags).forEach((k) => {
                m.columnFlags![k] = false;
              });
            }
            if (m.salesFlowActions) {
              Object.keys(m.salesFlowActions).forEach((k) => {
                m.salesFlowActions![k] = false;
              });
            }
          });
        }
      }
      // Actualizar lastFormDataRef inmediatamente para prevenir sobrescritura
      lastFormDataRef.current = JSON.stringify(newData);
      return newData;
    });
  }, []);

  const toggleMenu = useCallback((moduleId: string, menuId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      if (menu) {
        menu.visible = !menu.visible;
        // If disabling menu, disable all buttons and actions
        if (!menu.visible) {
          Object.keys(menu.mainButtons).forEach((buttonId) => {
            menu.mainButtons[buttonId] = false;
          });
          Object.keys(menu.rowActions).forEach((actionId) => {
            menu.rowActions[actionId] = false;
          });
          const m = menu as {
            columnFlags?: Record<string, boolean>;
            salesFlowActions?: Record<string, boolean>;
          };
          if (m.columnFlags) {
            Object.keys(m.columnFlags).forEach((k) => {
              m.columnFlags![k] = false;
            });
          }
          if (m.salesFlowActions) {
            Object.keys(m.salesFlowActions).forEach((k) => {
              m.salesFlowActions![k] = false;
            });
          }
        } else {
          syncBoundViewRowForMenuPermissions(menuId, menu);
        }
      }
      // Actualizar lastFormDataRef inmediatamente para prevenir sobrescritura
      lastFormDataRef.current = JSON.stringify(newData);
      return newData;
    });
  }, []);

  const toggleMenuVisibility = useCallback(
    (moduleId: string, menuId: string) => {
      toggleMenu(moduleId, menuId);
    },
    [toggleMenu],
  );

  const toggleMainButton = useCallback((moduleId: string, menuId: string, buttonId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      const button = menu?.mainButtons[buttonId];
      if (button !== undefined && menu) {
        menu.mainButtons[buttonId] = !button;
      }
      // Actualizar lastFormDataRef inmediatamente para prevenir sobrescritura
      lastFormDataRef.current = JSON.stringify(newData);
      return newData;
    });
  }, []);

  const toggleRowAction = useCallback((moduleId: string, menuId: string, actionId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      const action = menu?.rowActions[actionId];
      if (action !== undefined && menu) {
        const nextValue = !action;
        if (
          actionId === MENU_VIEW_ROW_ACTION_ID &&
          menuBindsViewRowToMenuVisibility(menuId) &&
          menu.visible &&
          !nextValue
        ) {
          return prev;
        }
        menu.rowActions[actionId] = nextValue;
      }
      // Actualizar lastFormDataRef inmediatamente para prevenir sobrescritura
      lastFormDataRef.current = JSON.stringify(newData);
      return newData;
    });
  }, []);

  const toggleColumnFlag = useCallback((moduleId: string, menuId: string, flagId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      if (menu) {
        if (!menu.columnFlags) {
          menu.columnFlags = {};
        }
        const flag = menu.columnFlags[flagId];
        if (flag !== undefined) {
          menu.columnFlags[flagId] = !flag;
        }
      }
      // Actualizar lastFormDataRef inmediatamente para prevenir sobrescritura
      lastFormDataRef.current = JSON.stringify(newData);
      return newData;
    });
  }, []);

  const toggleSalesFlowAction = useCallback(
    (moduleId: string, menuId: string, actionId: string) => {
      setFormData((prev) => {
        permissionsTouchedRef.current = true;
        const newData = JSON.parse(JSON.stringify(prev));
        const menu = newData.permissions[moduleId]?.menus[menuId];
        if (menu) {
          if (!menu.salesFlowActions) {
            menu.salesFlowActions = {};
          }
          const cur = menu.salesFlowActions[actionId];
          if (cur !== undefined) {
            menu.salesFlowActions[actionId] = !cur;
          }
        }
        lastFormDataRef.current = JSON.stringify(newData);
        return newData;
      });
    },
    [],
  );

  const selectAllMainButtons = useCallback((moduleId: string, menuId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      if (menu) {
        const allSelected = Object.values(menu.mainButtons).every((val) => val === true);
        Object.keys(menu.mainButtons).forEach((buttonId) => {
          menu.mainButtons[buttonId] = !allSelected;
        });
      }
      return newData;
    });
  }, []);

  const selectAllRowActions = useCallback((moduleId: string, menuId: string) => {
    setFormData((prev) => {
      permissionsTouchedRef.current = true;
      // Deep copy to avoid mutation
      const newData = JSON.parse(JSON.stringify(prev));
      const menu = newData.permissions[moduleId]?.menus[menuId];
      if (menu) {
        const allSelected = Object.values(menu.rowActions).every((val) => val === true);
        Object.keys(menu.rowActions).forEach((actionId) => {
          menu.rowActions[actionId] = !allSelected;
        });
      }
      return newData;
    });
  }, []);

  // Detectar si hay cambios sin guardar
  const hasChanges = useCallback((): boolean => {
    if (!initialFormDataRef.current) return false;

    if (!isEditModeRef.current) {
      // En modo creación, hay cambios si hay algún campo con contenido
      return formData.name.trim() !== '' || formData.description.trim() !== '';
    } else {
      // En modo edición, comparar con los valores iniciales
      const initial = initialFormDataRef.current;
      if (
        formData.name.trim() !== initial.name.trim() ||
        formData.description.trim() !== initial.description.trim()
      ) {
        return true;
      }
      // Comparar permisos (comparación profunda simplificada)
      const currentPermsStr = JSON.stringify(formData.permissions);
      const initialPermsStr = JSON.stringify(initial.permissions);
      return currentPermsStr !== initialPermsStr;
    }
  }, [formData]);

  // Computed values
  const totalPermissions = useMemo(() => {
    let count = 0;
    Object.values(formData.permissions).forEach((module) => {
      Object.values(module.menus).forEach((menu) => {
        if (menu.visible) count++;
        count += Object.values(menu.mainButtons).filter((val) => val).length;
        count += Object.values(menu.rowActions).filter((val) => val).length;
        if (menu.columnFlags) {
          count += Object.values(menu.columnFlags).filter((val) => val).length;
        }
        if (menu.salesFlowActions) {
          count += Object.values(menu.salesFlowActions).filter((val) => val).length;
        }
      });
    });
    return count;
  }, [formData.permissions]);

  const enabledModules = useMemo(() => {
    return Object.values(formData.permissions).filter((module) => module.enabled).length;
  }, [formData.permissions]);

  const enabledMenus = useMemo(() => {
    let count = 0;
    Object.values(formData.permissions).forEach((module) => {
      count += Object.values(module.menus).filter((menu) => menu.visible).length;
    });
    return count;
  }, [formData.permissions]);

  // Usar función de utilidades para convertir permisos
  const getPermissionsForAPI = useCallback(() => {
    return convertPermissionsToAPI(formData.permissions);
  }, [formData.permissions]);

  // Actions
  const handleSave = useCallback(async () => {
    const permissions = getPermissionsForAPI();
    await onSave({
      ...formData,
      permissions, // Enviar permisos convertidos al formato API
    } as any);
  }, [formData, getPermissionsForAPI, onSave]);

  const handleReset = useCallback(() => {
    setFormData(JSON.parse(JSON.stringify(INITIAL_FORM_DATA)));
    setCurrentStep(1);
  }, []);

  // Manejar el click en cerrar - mostrar confirmación si hay cambios
  const handleCloseClick = useCallback(() => {
    if (hasChanges()) {
      setShowCancelConfirmation(true);
    } else {
      // Si no hay cambios, cerrar directamente
      onClose();
    }
  }, [hasChanges, onClose]);

  // Cancelar operación - resetear y cerrar
  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    // Resetear formulario
    setFormData(JSON.parse(JSON.stringify(INITIAL_FORM_DATA)));
    setCurrentStep(1);
    initialFormDataRef.current = JSON.parse(JSON.stringify(INITIAL_FORM_DATA));
    isEditModeRef.current = false;
    onClose();
  }, [onClose]);

  return {
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    canGoNext,
    canGoPrev,
    formData,
    setFormData,
    toggleModule,
    toggleMenu,
    toggleMenuVisibility,
    toggleMainButton,
    toggleRowAction,
    toggleColumnFlag,
    toggleSalesFlowAction,
    selectAllMainButtons,
    selectAllRowActions,
    totalPermissions,
    enabledModules,
    enabledMenus,
    handleSave,
    handleReset,
    handleCloseClick,
    cancelOperation,
    showCancelConfirmation,
    setShowCancelConfirmation,
    hasChanges: hasChanges(),
  };
};
